import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/use-local-storage';

export const MIN_REPORT_ZOOM = 0.5;
export const MAX_REPORT_ZOOM = 1.5;

interface ZoomContextValue {
    zoom: number;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    handleZoomIn: () => void;
    handleZoomOut: () => void;
}

const ZoomContext = createContext<ZoomContextValue | null>(null);

/**
 * Holds the report preview zoom level in its own lightweight provider, isolated
 * from ReportWizardContext. Zoom changes only re-render this provider's
 * consumers (the zoom surface + floating controls), never the document tree, so
 * zooming a large report stays smooth.
 */
export const ZoomProvider = ({ children }: { children: React.ReactNode }) => {
    const [zoom, setZoom] = useLocalStorage('create_report_zoom_level', 1);

    const handleZoomIn = useCallback(
        () => setZoom((prev) => Math.min(prev + 0.1, MAX_REPORT_ZOOM)),
        [setZoom],
    );
    const handleZoomOut = useCallback(
        () => setZoom((prev) => Math.max(prev - 0.1, MIN_REPORT_ZOOM)),
        [setZoom],
    );

    const value = useMemo(
        () => ({ zoom, setZoom, handleZoomIn, handleZoomOut }),
        [zoom, setZoom, handleZoomIn, handleZoomOut],
    );

    return <ZoomContext.Provider value={value}>{children}</ZoomContext.Provider>;
};

export const useZoom = () => {
    const context = useContext(ZoomContext);
    if (!context) {
        throw new Error('useZoom must be used within a ZoomProvider');
    }
    return context;
};
